import { cn, PageHeader } from "@norican/react";
import type { ComponentPropsWithoutRef, ReactNode } from "react";

/**
 * How wide the page content is allowed to grow.
 *
 * `narrow` suits a single reading column (a conversation, a form), `default`
 * suits most pages, `wide` suits a dense table, `screen` suits an admin
 * table that must fit one screen (up to 1600 px, 12 Oct), `full` opts out
 * entirely. The first three resolve to the design system's container tokens.
 */
export type PageContainerWidth = "narrow" | "default" | "wide" | "screen" | "full";

const WIDTH_CLASS: Record<PageContainerWidth, string> = {
  narrow: "max-w-[var(--norican-size-container-sm)]",
  default: "max-w-[var(--norican-size-container-lg)]",
  wide: "max-w-[var(--norican-size-container-xl)]",
  screen: "max-w-[100rem]",
  full: "",
};

export interface PageContainerProps extends Omit<ComponentPropsWithoutRef<"div">, "title"> {
  /** Page heading. When set, a design system `PageHeader` is rendered above the content. */
  title?: ReactNode;
  /** Muted supporting line under the title. Ignored without a `title`. */
  subtitle?: ReactNode;
  /** Right-aligned action cluster in the header. Ignored without a `title`. */
  actions?: ReactNode;
  /** Slot above the title, typically `<Breadcrumbs>`. Ignored without a `title`. */
  breadcrumbs?: ReactNode;
  /** Content width. Defaults to `default`. */
  width?: PageContainerWidth;
  /** Tighter vertical rhythm, for a working screen that should fit one viewport. */
  dense?: boolean;
  children: ReactNode;
}

/**
 * The standard page wrapper: centred column, consistent gutters, generous
 * vertical rhythm, and an optional `PageHeader`.
 *
 * Every route inside the app shell should start with one of these so pages
 * line up with each other rather than each inventing its own padding.
 *
 *   <PageContainer title={t("title")} subtitle={t("subtitle")} width="wide">
 *     <SectionCard>…</SectionCard>
 *   </PageContainer>
 */
export function PageContainer({
  title,
  subtitle,
  actions,
  breadcrumbs,
  width = "default",
  dense = false,
  className,
  children,
  ...props
}: PageContainerProps) {
  return (
    <div
      className={cn(
        "mx-auto w-full px-4 sm:px-6 lg:px-8",
        dense ? "py-4" : "py-8",
        WIDTH_CLASS[width],
        className,
      )}
      {...props}
    >
      {title === undefined ? null : (
        <PageHeader
          className={dense ? "mb-3" : "mb-8"}
          title={title}
          subtitle={subtitle}
          actions={actions}
          breadcrumbs={breadcrumbs}
        />
      )}
      <div className={cn("flex flex-col", dense ? "gap-3" : "gap-6")}>{children}</div>
    </div>
  );
}
